import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { argentinaDate, PLAN_LABEL, planWith } from "../../shared/market";
import { ABOVE_MARKET_PCT, aboveMedianPct, FUNNEL_PAGE_SIZE, ORDERS_CSV_MAX_DAYS, type FunnelRow, type PriceReference } from "../../shared/marketStats";
import { LANGUAGE_LABEL } from "../../shared/variants";
import { Pagination } from "../components/Pagination";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { marketApi } from "./api";
import { useArs } from "./components";

const listingName = (l: { name: string; setName: string | null; cardNumber: string | null }) => [l.name, l.setName, l.cardNumber && `#${l.cardNumber}`].filter(Boolean).join(" · ");

/** "~3 horas": the same wording as the reputation's answer time. */
export function useDuration() {
  const { t } = useI18n();
  return (minutes: number | null) =>
    minutes === null ? "—" : minutes < 60 ? t("mkt.minutes", { count: Math.max(1, minutes) }) : minutes < 1440 ? t("mkt.hours", { count: Math.round(minutes / 60) }) : t("mkt.days", { count: Math.round(minutes / 1440) });
}

/**
 * Estadísticas › Cartas (EX and up): each listing from the visit to the sale, in the period. Under 480px
 * each row reads as a card (the cells name their column).
 */
export function Funnel({ rows, total, page, onPage, children }: { rows: FunnelRow[]; total: number; page: number; onPage: (p: number) => void; children?: ReactNode }) {
  const { t } = useI18n();
  const ars = useArs();
  return (
    <section aria-labelledby="ins-funnel">
      <h3 id="ins-funnel">{t("ins.funnel")}</h3>
      {children}
      <p className="muted small">{t("ins.funnelHint")}</p>
      {total === 0 ? (
        <p className="muted">{t("ins.funnelEmpty")}</p>
      ) : (
        <>
          <div className="imp-table-wrap">
            <table className="stats-table stats-table-cards">
              <thead>
                <tr>
                  <th scope="col">{t("imp.product")}</th>
                  <th scope="col" className="num">{t("st.colViews")}</th>
                  <th scope="col" className="num">{t("st.colFavorites")}</th>
                  <th scope="col" className="num">{t("ins.colOffers")}</th>
                  <th scope="col" className="num">{t("ins.colOrders")}</th>
                  <th scope="col" className="num">{t("ins.colUnits")}</th>
                  <th scope="col" className="num">{t("ins.colConversion")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <Link to={`/mercado/${l.id}`}>{listingName(l)}</Link>
                      <span className="muted small">
                        {" "}
                        · {ars(l.priceArs)}
                        {l.status === "paused" ? ` · ${t("bulk.paused")}` : ""}
                      </span>
                    </td>
                    <td className="num" data-label={t("st.colViews")}>{l.views}</td>
                    <td className="num" data-label={t("st.colFavorites")}>{l.favorites}</td>
                    <td className="num" data-label={t("ins.colOffers")}>{l.offers}</td>
                    <td className="num" data-label={t("ins.colOrders")}>{l.orders}</td>
                    <td className="num" data-label={t("ins.colUnits")}>{l.units}</td>
                    <td className="num" data-label={t("ins.colConversion")}>{l.conversionPct === null ? "—" : `${l.conversionPct.toLocaleString("es-AR")}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > FUNNEL_PAGE_SIZE && <Pagination page={page} totalPages={Math.ceil(total / FUNNEL_PAGE_SIZE)} onPage={onPage} />}
        </>
      )}
    </section>
  );
}

/** Where the listing's price stands: alone, the cheapest, or how far from the others' median. */
function verdict(r: PriceReference): { key: MessageKey; pct?: number; tone: "good" | "warn" | "" } {
  if (r.others === 0 || r.medianArs === null || r.minArs === null) return { key: "ins.alone", tone: "good" };
  if (r.priceArs <= r.minArs) return { key: "ins.cheapest", tone: "good" };
  const above = aboveMedianPct(r.priceArs, r.others, r.minArs, r.medianArs);
  if (above) return { key: "ins.above", pct: above, tone: above >= ABOVE_MARKET_PCT ? "warn" : "" };
  const pct = Math.round(((r.priceArs - r.medianArs) / r.medianArs) * 100);
  if (pct < 0) return { key: "ins.below", pct: -pct, tone: "good" };
  return { key: "ins.atMedian", tone: "" };
}

/** Estadísticas › Precios (EX and up): every live listing against the others' median, the dearest first; with what the store asks, when it has the card. */
export function Prices({ rows, storeOf }: { rows: PriceReference[]; storeOf: (id: number) => number | null }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  return (
    <section aria-labelledby="ins-prices">
      <h3 id="ins-prices">{t("ins.prices")}</h3>
      {rows.length === 0 ? (
        <p className="muted">{t("ins.pricesEmpty")}</p>
      ) : (
        <ul className="stats-list">
          {rows.map((r) => {
            const v = verdict(r);
            const store = storeOf(r.id);
            return (
              <li key={r.id}>
                <Link to={`/mercado/${r.id}`}>{listingName(r)}</Link>{" "}
                <span className="muted small">
                  ({r.condition} · {LANGUAGE_LABEL[r.language]?.[locale] ?? r.language})
                </span>{" "}
                <span className={v.tone === "good" ? "pill pill-paid" : v.tone === "warn" ? "pill pill-unpaid" : "pill"}>{t(v.key, { pct: v.pct ?? 0 })}</span>
                {r.others > 0 && r.minArs !== null && r.medianArs !== null && (
                  <span className="muted small"> {t("ins.priceRow", { mine: ars(r.priceArs), median: ars(r.medianArs), min: ars(r.minArs), count: r.others })}</span>
                )}
                {store !== null && <span className="muted small"> {t("st.pricingStore", { store: ars(store) })}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/** In Ventas: the orders between two dates as a CSV for Excel (EX and up), or which plan has it. */
export function OrdersExport({ allowed }: { allowed: boolean }) {
  const { t } = useI18n();
  const today = argentinaDate(new Date());
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  if (!allowed) return <p className="muted small">{t("csv.teaser", { plan: PLAN_LABEL[planWith("insights")] })}</p>;
  // As the server takes it: both dates, in order, under a year apart.
  const valid = from !== "" && to !== "" && from <= to && (Date.parse(to) - Date.parse(from)) / 86_400_000 < ORDERS_CSV_MAX_DAYS;
  return (
    <section className="plan-card orders-export" aria-labelledby="csv-title">
      <h2 id="csv-title" className="small-title">
        {t("csv.title")}
      </h2>
      <p className="muted small">{t("csv.hint")}</p>
      <div className="btn-row">
        <label>
          {t("csv.from")} <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          {t("csv.to")} <input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
        </label>
        {valid ? (
          <a className="btn btn-outline btn-sm" href={marketApi.ordersCsvUrl(from, to)} download>
            {t("csv.download")}
          </a>
        ) : (
          <button type="button" className="btn btn-outline btn-sm" disabled>
            {t("csv.download")}
          </button>
        )}
      </div>
    </section>
  );
}
